import { useCallback, useEffect, useState } from 'react'
import useDebouncedEvent from '~hooks/use-debounced-event'
import useMyProfile from '~repositories/hooks/use-my-profile'
import { OWNER_NAME_MIN_LENGTH, validateOwnerName } from '~utils/validations'

const useOwnerName = () => {
  const { data: myProfile } = useMyProfile()
  const [ownerName, setOwnerName] = useState<string>('')
  const [ownerNameErrorMessage, setOwnerNameErrorMessage] = useState<string>('')
  const [isOwnerNameFocused, setOwnerNameFocused] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const originalOwnerName = myProfile?.nickname

  const hasOwnerNameChanged = ownerName !== originalOwnerName
  const hasOwnerNameError = !!ownerNameErrorMessage?.length

  const ownerNameHelperMessage = hasOwnerNameError
    ? ownerNameErrorMessage
    : isOwnerNameFocused && !hasOwnerNameChanged && !hasOwnerNameError
      ? `Owner name must be between ${OWNER_NAME_MIN_LENGTH} and 20 characters long.`
      : ''

  const checkOwnerName = useDebouncedEvent(async (value: string) => {
    const errorMessage = validateOwnerName(value)

    if (errorMessage) {
      setIsLoading(false)
      return setOwnerNameErrorMessage(errorMessage)
    }

    setIsLoading(false)
    setOwnerNameErrorMessage('')
  })

  const handleOwnerName = useCallback(
    (value: string) => {
      setIsLoading(true)
      setOwnerName(value)

      if (value === originalOwnerName) {
        setIsLoading(false)
        return setOwnerNameErrorMessage('')
      }

      checkOwnerName(value)
      setOwnerNameFocused(true)
    },
    [checkOwnerName, originalOwnerName]
  )

  useEffect(
    function initOwnerName() {
      if (!myProfile) {
        return
      }

      setOwnerName(myProfile.nickname)
    },
    [myProfile]
  )

  return {
    ownerName,
    ownerNameHelperMessage,
    hasOwnerNameChanged,
    hasOwnerNameError,
    isLoading,
    setOwnerNameFocused,
    handleOwnerName,
  }
}

export default useOwnerName
